// The step rail (app/art/welcome/PLAN.md sections 4.1 and 4.4): the stops of
// creating or restoring a vault, as one ordered list. Welcome previews the
// stops Create then walks through, so the end is visible before the start.
// - preview: five equal columns, a bar, a label and a sub; nothing is current
//   and nothing reacts to the pointer, so it never reads as clickable;
// - progress (Create, Restore, /vault): the current stop has a mint bar and
//   aria-current="step", done stops a faint mint bar, stops that happen on
//   /vault a fainter one; each stop also says its state in words, so colour
//   is never the only signal.
// On phones the preview turns vertical and the progress labels are hidden
// visually (still read aloud); the step counter beside the rail names the stop.

export type Flow = 'create' | 'restore' | 'restore-new';

export const STEPS: Record<Flow, readonly string[]> = {
  create: ['Words', 'Confirm', 'Passphrase', 'Fee reserve', 'Activate'],
  restore: ['Words', 'Check', 'Passphrase', 'Key refresh'],
  'restore-new': ['Words', 'Check', 'Passphrase', 'Fee reserve', 'Activate'],
};

/** The first stop that happens on /vault rather than in the flow's own screens. */
const ON_VAULT_FROM: Record<Flow, number | null> = { create: 4, restore: null, 'restore-new': 4 };

const FLOW_NAME: Record<Flow, string> = { create: 'Creating a vault', restore: 'Restoring a vault', 'restore-new': 'Restoring a vault' };
const COUNT_WORD: Record<number, string> = { 4: 'four', 5: 'five' };

export function StepRail({ flow, current, preview, subs }: { flow: Flow; /** 1-based; omitted in preview. */ current?: number; preview?: boolean; /** One line under each label (preview). */ subs?: readonly string[] }) {
  const steps = STEPS[flow];
  const columns = `n${steps.length}`;
  if (preview || current === undefined) {
    return (
      <ol className={`rail preview ${columns}`} aria-label={`${FLOW_NAME[flow]}, ${COUNT_WORD[steps.length] ?? steps.length} steps`}>
        {steps.map((label, i) => (
          <li key={label}>
            <span className="lbl">{label}</span>
            {subs?.[i] ? <span className="sub2">{subs[i]}</span> : null}
          </li>
        ))}
      </ol>
    );
  }
  const later = ON_VAULT_FROM[flow];
  return (
    <ol className={`rail prog ${columns}`} aria-label={FLOW_NAME[flow]}>
      {steps.map((label, i) => {
        const n = i + 1;
        const here = n === current;
        const done = n < current;
        const word = here ? ', current step' : done ? ', done' : ', not started';
        return (
          <li key={label} aria-current={here ? 'step' : undefined} className={done ? 'done' : !here && later !== null && n >= later ? 'later' : undefined}>
            <span className="lbl">
              {label}
              <span className="sr-only">{word}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
